import { VOMedia } from "../model/DataStructure.js";
import { Icon } from "./Icon.js";
import { Media } from "./Media.js";

export interface MediaPanelOptions {
    container: HTMLElement;
    visible?: boolean;
    assetsUrl: string;
}

export class MediaPanel {

    private _element: HTMLDivElement;
    private _options: MediaPanelOptions;
    private _media: Media;
    private _data: VOMedia;

    constructor(options: MediaPanelOptions) {
        options = e5.object.merge({
            visible: false
        }, options);
        this._options = options;

        this._element = document.createElement("div");
        this._element.className = "media-panel base__panel base__panel--hidden";
        this._element.innerHTML = `
        <div class="media-panel__header">
            <div class="media-panel__nav">
                <div class="media-panel__nav-item media-panel__nav-item--prev">
                    <div class="media-panel__nav-item-button ${Icon.ARROW_LEFT}" data-action="prev"></div>
                    <div class="media-panel__nav-item-label"></div>
                </div>
                <div class="media-panel__nav-item media-panel__nav-item--next">
                    <div class="media-panel__nav-item-label"></div>
                    <div class="media-panel__nav-item-button ${Icon.ARROW_RIGHT}" data-action="next"></div>
                </div>
            </div>
        </div>
        <div class="media-panel__body">
            <div class="media-panel__media"></div>
            <div class="media-panel__text">
                <div class="media-panel__exhibition-name"></div>
                <div class="media-panel__media-name"></div>
                <div class="media-panel__media-description"></div>
            </div>
        </div>
        `;

        this._media = new Media({
            container: this._element.querySelector(".media-panel__media"),
            visible: true
        });

        this.visible = options.visible;

        e5.event.on({
            scope: this._element,
            type: "click",
            selector: `[data-action="prev"]`,
            listener: () => {
                this.prev();
            }
        });

        e5.event.on({
            scope: this._element,
            type: "click",
            selector: `[data-action="next"]`,
            listener: () => {
                this.next();
            }
        });

        options.container.appendChild(this._element);
    }

    private prev(): void {
        let medias = this._data?.exhibition?.medias;
        if (!medias) {
            return;
        }
        this.load(medias[e5.Calc.wrap(medias.indexOf(this._data) - 1, medias.length)]);
    }

    private next(): void {
        let medias = this._data?.exhibition?.medias;
        if (!medias) {
            return;
        }
        this.load(medias[e5.Calc.wrap(medias.indexOf(this._data) + 1, medias.length)]);
    }

    public load(m: VOMedia): void {
        this._data = m;

        this._element.querySelector(".media-panel__media-description")
            .scrollTop = 0;

        this._media.load(m);

        let exhibitionName = m.exhibition.name;
        if (m.exhibition.subline) {
            exhibitionName += " - " + m.exhibition.subline;
        }
        this._element.querySelector(".media-panel__exhibition-name")
            .textContent = exhibitionName;

        this._element.querySelector(".media-panel__media-name")
            .textContent = m.name;

        this._element.querySelector(".media-panel__media-description")
            .textContent = m.description;

        const medias = m.exhibition.medias;
        const showNav = medias.length > 1;
        this._element.querySelector(".media-panel__nav")
            .classList.toggle("e5-hidden", !showNav);
        if (showNav) {
            const showNavPrev = medias.length > 2;
            this._element.querySelector(".media-panel__nav-item--prev")
                .classList.toggle("e5-hidden", !showNavPrev);

            if (showNavPrev) {
                let mediaIndex = e5.Calc.wrap(medias.indexOf(m) - 1, medias.length);
                let prev = medias[mediaIndex];
                this._element.querySelector(".media-panel__nav-item--prev")
                    .setAttribute("data-mediaindex", mediaIndex + "");
                this._element.querySelector(".media-panel__nav-item--prev > .media-panel__nav-item-label")
                    .textContent = prev.name;
            }

            let mediaIndex = e5.Calc.wrap(medias.indexOf(m) + 1, medias.length);
            let next = medias[mediaIndex];

            this._element.querySelector(".media-panel__nav-item--next")
                .setAttribute("data-mediaindex", mediaIndex + "");
            this._element.querySelector(".media-panel__nav-item--next > .media-panel__nav-item-label")
                .textContent = next.name;
        }
    }

    public get visible(): boolean {
        return !this._element.classList.contains("base__panel--hidden");
    }

    public set visible(value: boolean) {
        this._element.classList.toggle("base__panel--hidden", !value);
        this._media.visible = value;
    }
}